// Vuex
import { ActionTree } from 'vuex'

// Firebase
import firebase from 'firebase/app'

// Vuex Fire
import { firestoreAction } from 'vuexfire'

// Db
import { db } from '@/database'

// Vuex Root state
import { State as RootState } from '@/store/state'
// Module state
import { MatchState } from './state'

// Libraries
import {
  currentPeriodNumber,
  timeUnboxedNumericValue,
  totalNumberOfPeriods,
} from 'match-central-shared/dist/game-clock'

export default <ActionTree<MatchState, RootState>>{
  /**
   * Bind match as firestore ref
   * Inspiration from:
   * https://dev.to/rajesh5522/how-to-use-vuexfire-vue-notes-applicaiton-e44
   */
  bindMatch: firestoreAction(({ bindFirestoreRef }, matchId) => {
    // console.log('matchId', matchId)
    return bindFirestoreRef('match', db.collection('matches').doc(matchId))
  }),

  // Unbind firestore ref
  unbindMatch: firestoreAction(({ unbindFirestoreRef }) => {
    return unbindFirestoreRef('match')
  }),

  /**
   * Decrease score
   */
  decreaseScore({ dispatch }, team: 'h' | 'v') {
    // console.log('Decrease score for team', team)

    dispatch('changeScore', [team, -1])
  },

  /**
   * Increase score
   */
  increaseScore({ dispatch }, team: 'h' | 'v'): void {
    // console.log('Increase score for team', team)

    dispatch('changeScore', [team, 1])
  },

  /**
   * Change score
   */
  changeScore: firestoreAction(({ state }, [team, diff]) => {
    if (!state.match) {
      return
    }

    // console.log('Change score', team, diff)

    const fieldName = `${team}Score` as 'hScore' | 'vScore'

    const obj: { hScore?: number; vScore?: number } = {}
    obj[fieldName] = ((state.match[fieldName] as number) || 0) + diff

    // Persist updated score to firestore
    return (
      db
        .collection('matches')
        .doc(state.match.id)
        .update(obj)
        .then((val: unknown) => {
          // console.log(`Match updated! 'score changed'`)
          return val
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_e: unknown) => {
          // console.error('Error updating match in firestore', _e)
          window.flash('Error changing score...', 'error')
        })
    )
  }),

  /**
   * Logic for setting clock
   */
  setClock: firestoreAction(({ state }, value) => {
    if (!state.match) {
      return
    }
    // console.log('New clock state', newVal)

    // Persist updated game clock to firestore
    return (
      //
      db
        .collection('matches')
        .doc(state.match.id)
        .update(value)
        .then((ret: unknown) => {
          return ret
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_e: unknown) => {
          window.flash('Error setting clock...', 'error')
          // console.error('Error updating match clock in firestore', _e)
        })
    )
  }),

  /**
   * Start clock
   */
  startClock: firestoreAction(({ state, dispatch }, adjustSec = 0) => {
    // console.log('Vuex action: match start clock')

    if (!state.match) {
      return
    }

    const newVal: number =
      timeUnboxedNumericValue(state.match, state.realtime.timestamp) + adjustSec

    // console.log('New clock state', newVal)

    const newGcObj = {
      gcState: 'STARTED',
      gcLastValue: newVal,
      gcLastStarted: firebase.firestore.FieldValue.serverTimestamp(),
    }

    // Persist updated game clock to firestore
    return (
      dispatch('setClock', newGcObj)
        .then(() => {
          // console.log(`Match updated 'game clock started'!`)

          dispatch('startRealtimeInterval')
          return newVal
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_e: unknown) => {
          // console.error('Error updating match game clock in firestore', _e)
          window.flash('Error setting clock....', 'error')
        })
    )
  }),

  /**
   * Pause clock
   */
  pauseClock: firestoreAction(({ state, commit, dispatch }, adjustSec = 0) => {
    // console.log('Vuex firestore action: match pause clock')
    if (!state.match) {
      return
    }

    const newVal: number =
      timeUnboxedNumericValue(state.match, state.realtime.timestamp) + adjustSec

    // console.log('New clock state', newVal)
    const newGcObj = {
      gcState: 'PAUSED',
      gcLastValue: newVal,
      gcLastPaused: firebase.firestore.FieldValue.serverTimestamp(),
    }

    // Persist updated clock to firestore
    return (
      dispatch('setClock', newGcObj)
        .then(() => {
          // console.log(`Match updated 'game clock paused'!`)
          commit('stopRealtimeInterval')
          return newVal
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_e: unknown) => {
          // console.error('Error updating match in firestore', _e)
          window.flash('Error setting clock', 'error')
        })
    )
  }),

  /**
   * Goto previous period
   */
  gotoPreviousPeriod: firestoreAction(({ state, commit, dispatch }) => {
    if (!state.match) {
      return
    }

    const newPeriodNumber = Math.max(1, currentPeriodNumber(state.match) - 1)

    const newGcObj = {
      gcState: 'PAUSED',
      gcCurrentPeriod: newPeriodNumber,
      gcLastValue: 0,
      gcLastPaused: firebase.firestore.FieldValue.serverTimestamp(),
    }

    // console.log('New clock state', newGcObj)

    // Persist updated game clock to firestore
    return (
      dispatch('setClock', newGcObj)
        .then(() => {
          // console.log(`Match updated 'game clock started'!`)
          commit('stopRealtimeInterval')
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_e: unknown) => {
          // console.error('Error updating match game clock in firestore', _e)
        })
    )
  }),

  /**
   * Force Goto next period
   * DO NOT GUARD THAT PERIOD IS NOT FINISHED
   */
  forceGotoNextPeriod: firestoreAction(({ state, dispatch, commit }) => {
    // Guard no match
    if (!state.match) {
      return
    }

    // console.log('Go to next period')

    // Update real time date to ensure
    // the presented clock upon start of clock is not 'flashing' with old value
    commit('refreshRealtimeTimestamp')

    const newPeriodNumber = Math.min(
      // Can max go to period after last overtime period (or last regular period if no overtime periods)
      totalNumberOfPeriods(state.match) + 1,
      currentPeriodNumber(state.match) + 1
    )

    const newGcObj = {
      gcState: 'PAUSED',
      gcCurrentPeriod: newPeriodNumber,
      gcLastValue: 0,
      gcLastPaused: firebase.firestore.FieldValue.serverTimestamp(),
    }

    // console.log('New clock state', newGcObj)

    // Persist updated game clock to firestore
    return (
      dispatch('setClock', newGcObj)
        .then(() => {
          // console.log(`Match updated 'game clock started'!`)
          commit('stopRealtimeInterval')
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .catch((_e: unknown) => {
          // console.error('Error updating match game clock in firestore', _e)
        })
    )
  }),

  /**
   * Goto next period
   * GUARDS PERIOD MUST BE FINISHED
   */
  gotoNextPeriod: firestoreAction(({ state, dispatch, getters }) => {
    // Guard no match
    if (!state.match) {
      return
    }

    if (!getters.isAtEndOfCurrentPeriod) {
      return
    }

    dispatch('forceGotoNextPeriod')
  }),

  startRealtimeInterval({ state, dispatch, commit }) {
    // Guard already started
    if (state.realtime.interval !== null) {
      return
    }

    commit(
      'setRealtimeInterval',
      setInterval(() => {
        state.realtime.timestamp = firebase.firestore.Timestamp.now()
        dispatch('checkHasReachedEndOfPeriod')
      }, 100)
    )

    // console.log('Realtime interval started')
  },

  stopRealtimeInterval({ commit }) {
    commit('stopRealtimeInterval')
  },

  /**
   * Check whether game clock time has reached end of period
   * Then Automatic pause period
   */
  checkHasReachedEndOfPeriod({ state, dispatch, getters }) {
    if (!state.match) {
      return
    }

    if (getters.isAtEndOfCurrentPeriod) {
      // console.log('Is at end of current period. Pauses clock.')
      dispatch('pauseClock')
    }
  },
}
